import { Navigate, Route } from 'react-router';

import DynamicImport from 'components/core/DynamicImport';
import CoreLayout from 'components/ui/layouts/CoreLayout';
import { RoutePath, SubRoute } from 'enums/routePath';
import { nonWhiteLabelScopes, ROOFTOPS_BASIC_PERMISSIONS } from 'utils/permissionUtils';

import { AccessLevel, ResourceType, UserScope } from 'store/client/graphql';

import CoreRoute from './CoreRoute';
import RestrictedRoute from './RestrictedRoute';
import SettingRoutes from './SettingsRoutes';
import TradeInLegacyRedirect from './TradeInLegacyRedirect';

/**
 * Auth containers
 * No prefetch - loaded on demand only
 */
const Login = DynamicImport(() => import('containers/auth/Login'));
const ForgotPassword = DynamicImport(() => import('containers/auth/ForgotPassword'));
const ResetPassword = DynamicImport(() => import('containers/auth/ResetPassword'));

/**
 * Main feature containers
 * Dashboard uses preload for immediate loading after auth
 * Other routes use prefetch for background loading when browser is idle
 */
const DashboardController = DynamicImport(
  () => import(/* webpackPreload: true */ 'containers/dashboard/DashboardController')
);
const AppointmentsContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/appointments/AppointmentsContainer')
);
const ConversationsContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/conversations/ConversationsContainer')
);
const LeadActivitiesContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/leadActivities/LeadActivitiesContainer')
);
const TasksContainer = DynamicImport(() => import(/* webpackPrefetch: true */ 'containers/tasks/TasksContainer'));
const RetailItemsContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/inventoryItems/RetailItemsContainer')
);
const TradeInItemsContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/inventoryItems/TradeInItemsContainer')
);
const LeadsContainer = DynamicImport(() => import(/* webpackPrefetch: true */ 'containers/leads/LeadsContainer'));
const UsersContainer = DynamicImport(() => import(/* webpackPrefetch: true */ 'containers/users/UsersContainer'));
const RooftopsContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/rooftops/RooftopsContainer')
);
const WebsitesContainer = DynamicImport(
  () => import(/* webpackPrefetch: true */ 'containers/websites/WebsitesContainer')
);

const MobileRichTextEditorContainer = DynamicImport(() =>
  import('containers/editor/MobileRichTextEditorContainer').then(module => ({
    default: module.MobileRichTextEditorContainer,
  }))
);

const PaymentProviderIntegrationRefresh = DynamicImport(
  () => import('containers/paymentProvider/PaymentProviderIntegrationRefresh')
);

const Error404 = DynamicImport(() => import('components/core/errors/Error404'));

const MockInfo = DynamicImport(() => import('components/pages/MockInfo'));

/**
 * Rationale for Route composition:
 * https://gist.github.com/mjackson/d54b40a094277b7afdd6b81f51a0393f
 */
export default (
  <Route element={<CoreRoute />}>
    <Route
      element={<Navigate replace to={RoutePath.DASHBOARD} />}
      handle={{ pathPattern: RoutePath.HOME }}
      path={RoutePath.HOME}
    />

    <Route
      element={<MobileRichTextEditorContainer />}
      handle={{ pathPattern: RoutePath.MOBILE_RICH_TEXT_EDITOR }}
      path={RoutePath.MOBILE_RICH_TEXT_EDITOR}
    />

    <Route element={<Login />} handle={{ pathPattern: RoutePath.LOGIN }} path={RoutePath.LOGIN} />
    <Route
      element={<ForgotPassword />}
      handle={{ pathPattern: RoutePath.FORGOT_PASSWORD }}
      path={RoutePath.FORGOT_PASSWORD}
    />
    <Route
      element={<ResetPassword />}
      handle={{ pathPattern: `${RoutePath.RESET_PASSWORD}/:id` }}
      path={`${RoutePath.RESET_PASSWORD}/*`}
    />

    <Route
      element={
        <CoreLayout>
          <DashboardController />
        </CoreLayout>
      }
      handle={{ pathPattern: RoutePath.DASHBOARD }}
      path={RoutePath.DASHBOARD}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.APPOINTMENTS }]}>
          <AppointmentsContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.APPOINTMENTS}/:id` }}
      path={`${RoutePath.APPOINTMENTS}/*`}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.LEADS }]}>
          <ConversationsContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.CONVERSATIONS}/:id` }}
      path={`${RoutePath.CONVERSATIONS}/*`}
    />

    <Route
      element={
        <RestrictedRoute
          authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.LEADS }]}
          authorizedUserScopes={nonWhiteLabelScopes}
        >
          <LeadActivitiesContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.LEAD_ACTIVITIES}/:id` }}
      path={`${RoutePath.LEAD_ACTIVITIES}/*`}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.TASKS }]}>
          <TasksContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.TASKS}/:id` }}
      path={`${RoutePath.TASKS}/*`}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.RETAIL_ITEMS }]}>
          <RetailItemsContainer />
        </RestrictedRoute>
      }
      /**
       * Mainly use for analytics purposes to track path with `/retail/:id` instead of `/retail/?` or `/retail/<real-id>`
       * https://github.com/DataDog/rum-react-integration-examples/tree/master/src/Router#overview
       *
       * Work around to retrieve path pattern for each route until React-Router decides
       * to expose this somehow. https://github.com/remix-run/react-router/issues/8785#issuecomment-1251719772
       */
      handle={{ pathPattern: `${RoutePath.RETAIL_ITEMS}/:id` }}
      path={`${RoutePath.RETAIL_ITEMS}/*`}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.TRADE_IN_ITEMS }]}>
          <TradeInItemsContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.TRADE_IN_ITEMS}/:id` }}
      path={`${RoutePath.TRADE_IN_ITEMS}/*`}
    />

    <Route element={<TradeInLegacyRedirect />} path="/trade-ins-new/*" />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.LEADS }]}>
          <LeadsContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.LEADS}/:id` }}
      path={`${RoutePath.LEADS}/*`}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.USERS }]}>
          <UsersContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.USERS}/:id` }}
      path={`${RoutePath.USERS}/*`}
    />

    <Route
      element={
        <RestrictedRoute
          authorizedPermissions={ROOFTOPS_BASIC_PERMISSIONS}
          authorizedUserScopes={[UserScope.GLOBAL]}
          redirectPath={parameters => {
            const rooftopSubRoute = parameters['*'];
            const rooftopId = rooftopSubRoute?.replace('table/', '');

            return rooftopId
              ? `${RoutePath.SETTINGS}/${SubRoute.ROOFTOP}/${rooftopId}`
              : `${RoutePath.SETTINGS}/${SubRoute.ROOFTOP}`;
          }}
        >
          <RooftopsContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.ROOFTOPS}/:id` }}
      path={`${RoutePath.ROOFTOPS}/*`}
    />

    <Route
      element={<PaymentProviderIntegrationRefresh />}
      handle={{ pathPattern: RoutePath.PAYMENT_PROVIDER_INTEGRATION_REFRESH }}
      path={RoutePath.PAYMENT_PROVIDER_INTEGRATION_REFRESH}
    />

    <Route
      element={<Navigate to={`${RoutePath.SETTINGS}/${SubRoute.ACCOUNT}`} />}
      handle={{ pathPattern: RoutePath.MY_ACCOUNT }}
      path={RoutePath.MY_ACCOUNT}
    />

    <Route
      element={
        <RestrictedRoute authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.WEBSITES }]}>
          <WebsitesContainer />
        </RestrictedRoute>
      }
      handle={{ pathPattern: `${RoutePath.WEBSITES}/:id` }}
      path={`${RoutePath.WEBSITES}/*`}
    />

    {SettingRoutes()}

    {process.env.REACT_APP_FEATURE_DEBUG_ROUTES_ENABLED === 'true' && (
      <Route element={<MockInfo />} handle={{ pathPattern: RoutePath.MOCKS }} path={RoutePath.MOCKS} />
    )}

    <Route
      element={
        <CoreLayout>
          <Error404 />
        </CoreLayout>
      }
      path="*"
    />
  </Route>
);
